import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChoiceButton } from "@/components/ChoiceButton";
import { Loader2, Tags } from "lucide-react";

/* HIS SMALL NAMING REPORT (23 Oct): across the latest multi-tag read of every live part, how many tags a person has
   named and how many still carry the AI's caption. Whole-shop totals, inside the app, behind the login. */
const n = (x) => (x || 0).toLocaleString();

export function TagNamingReport() {
  const { has } = useAuth();
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/catch-up/tag-naming").then(({ data }) => setR(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not count the tag names"));
  }, []);
  if (error) return <div data-testid="tag-naming-error" className="text-sm text-[#B01E28]">{error}</div>;
  if (!r) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Counting the tag names…</div>;
  const pct = r.tags ? Math.round((100 * r.named_by_a_person) / r.tags) : 0;
  return (
    <div data-testid="tag-naming-report" className="rounded-lg border-2 border-sky-200 bg-sky-50/60 p-3 flex items-start gap-3">
      <Tags className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1 text-sm text-slate-700 space-y-1.5">
        <p>
          <span className="font-bold text-[#1F1F1F]">Tag names (Ask AI):</span>{" "}
          <b data-testid="tag-naming-by-person" className="text-sky-800 tabular-nums">{n(r.named_by_a_person)}</b> named by a person ·{" "}
          <b data-testid="tag-naming-on-ai" className="text-slate-800 tabular-nums">{n(r.on_the_ai_caption)}</b> still on the AI's caption
          <span className="text-slate-500"> — of <span data-testid="tag-naming-total">{n(r.tags)}</span> tags on {n(r.parts)} part(s)</span>
        </p>
        <div className="h-2 rounded-full bg-white border border-sky-200 overflow-hidden max-w-md">
          <div data-testid="tag-naming-bar" className="h-full bg-sky-600 transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[12px] text-slate-500">
          {r.waiting_for_approval > 0 && <span data-testid="tag-naming-waiting">{n(r.waiting_for_approval)} named tag(s) use a name waiting for approval ({n(r.names_waiting)} name(s) waiting). </span>}
          {r.names_waiting > 0 && has("manage_lists") && <ChoiceButton tone="sky" asChild><Link data-testid="tag-naming-approve-link" to="/managed-lists">Approve or merge them on Managed Lists →</Link></ChoiceButton>}
          {" "}Counts the latest read with two or more tags on each live part. Counted in {n(r.counted_in_ms)} ms.
        </p>
      </div>
    </div>
  );
}
